'use client'

import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'

interface StoryPointInputProps {
  value: number | null
  onChange: (points: number | null) => void
  min?: number
  max?: number
  fibonacci?: boolean
  disabled?: boolean
  label?: string
  showLabel?: boolean
  mode?: 'input' | 'select'
}

const FIBONACCI_SEQUENCE = [1, 2, 3, 5, 8, 13, 21, 34, 55, 89]

export function StoryPointInput({
  value,
  onChange,
  min = 0,
  max = 100,
  fibonacci = false,
  disabled = false,
  label = 'Story Points',
  showLabel = true,
  mode = 'select'
}: StoryPointInputProps) {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    if (inputValue === '') {
      onChange(null)
      return
    }

    const numValue = parseInt(inputValue, 10)

    if (isNaN(numValue)) {
      return
    }

    if (numValue < min || numValue > max) {
      return
    }

    onChange(numValue)
  }

  const handleSelectChange = (newValue: string) => {
    if (newValue === 'none') {
      onChange(null)
    } else {
      onChange(parseInt(newValue, 10))
    }
  }

  const getAvailableValues = (): number[] => {
    if (fibonacci) {
      return FIBONACCI_SEQUENCE.filter(n => n >= min && n <= max)
    }

    // For non-fibonacci, create a reasonable range
    const values: number[] = []
    const step = max > 20 ? 5 : 1
    for (let i = min; i <= Math.min(max, 20); i += step) {
      values.push(i)
    }
    return values
  }

  if (mode === 'select' || fibonacci) {
    const availableValues = getAvailableValues()

    return (
      <div className="space-y-2">
        {showLabel && <Label>{label}</Label>}
        <Select
          value={value?.toString() || 'none'}
          onValueChange={handleSelectChange}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select story points..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <span className="text-muted-foreground">No estimate</span>
            </SelectItem>
            {availableValues.map((points) => (
              <SelectItem key={points} value={points.toString()}>
                {points} {points === 1 ? 'point' : 'points'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {showLabel && <Label htmlFor="story-points">{label}</Label>}
      <div className="relative">
        <Input
          id="story-points"
          type="number"
          min={min}
          max={max}
          value={value ?? ''}
          onChange={handleInputChange}
          disabled={disabled}
          placeholder="Enter story points"
          className="pr-16"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-sm text-muted-foreground">
          {value !== null && (value === 1 ? 'point' : 'points')}
        </div>
      </div>
    </div>
  )
}

/**
 * Inline story point badge component for displaying story points on cards
 */
interface StoryPointBadgeProps {
  points: number | null
  size?: 'sm' | 'md' | 'lg'
}

export function StoryPointBadge({ points, size = 'sm' }: StoryPointBadgeProps) {
  if (points === null || points === undefined) {
    return (
      <div className={`inline-flex items-center justify-center rounded-full border-2 border-dashed border-muted-foreground/30 text-muted-foreground ${
        size === 'sm' ? 'h-6 w-6 text-xs' : size === 'md' ? 'h-8 w-8 text-sm' : 'h-10 w-10 text-base'
      }`}>
        ?
      </div>
    )
  }

  // Color coding by size
  let colorClass = 'bg-green-100 text-green-800 border-green-300'
  if (points >= 13) {
    colorClass = 'bg-red-100 text-red-800 border-red-300'
  } else if (points >= 5) {
    colorClass = 'bg-yellow-100 text-yellow-800 border-yellow-300'
  }

  return (
    <div className={`inline-flex items-center justify-center rounded-full border ${colorClass} ${
      size === 'sm' ? 'h-6 w-6 text-xs font-medium' : size === 'md' ? 'h-8 w-8 text-sm font-semibold' : 'h-10 w-10 text-base font-bold'
    }`}>
      {points}
    </div>
  )
}
